import Add from "@mui/icons-material/Add";
import Grading from "@mui/icons-material/Grading";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import QuestionAnswer from "@mui/icons-material/QuestionAnswer";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import React, { Dispatch, SetStateAction, useCallback, useRef } from "react";
import { Status } from "../../interfaces/common";
import { AssetAttrsDetails, ProcessingAttrs } from "../../interfaces/orion";
import ExportMenu from "../Orion/ExportMenu";
import { useAddDocumentsAsset, useUpdateWorkflow } from "../Orion/OrionQueries";
import ProcessDialog from "../useDialogs/Orion/ProcessDialog";
import ProcessingHistoryDialog from "../useDialogs/Orion/ProcessingHistoryDialog";
import { useDialogs } from "../useDialogs/useDialogs";
import PageHeader from "./PageHeader";
import PageHeaderToolbar from "./PageHeaderToolbar";

function AssetPageToolbar({
  asset,
  processing,
  setProcessingId,
  displayMode,
  setDisplayMode,
}: {
  asset: AssetAttrsDetails;
  processing: ProcessingAttrs | undefined;
  setProcessingId: Dispatch<SetStateAction<string | undefined>>;
  displayMode: "chat" | "document";
  setDisplayMode: Dispatch<SetStateAction<"chat" | "document">>;
}) {
  const dialogs = useDialogs();

  const { mutate: updateWorkflow } = useUpdateWorkflow();
  const { mutate: addDocuments } = useAddDocumentsAsset();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(event.target.files || []);
    addDocuments({ assetId: asset!._id, files: uploadedFiles });
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    if (!processing) return;
    updateWorkflow({
      processingId: processing._id,
      assetId: asset!._id,
      workflow: event.target.value as string,
    });
  };

  return (
    <PageHeaderToolbar>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          mt: { xs: 1, md: 0 },
        }}
      >
        <Tooltip title="Relancer le traitement">
          <IconButton
            color="primary"
            size="small"
            onClick={() =>
              dialogs.open(ProcessDialog, {
                assetId: asset._id,
                workspaceId: asset.workspace_id,
                configId: processing?.config_char?._id,
              })
            }
          >
            <PlayCircleOutline />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            displayMode === "chat"
              ? "Voir les résultats du traitement"
              : "Voir le chat"
          }
        >
          <IconButton
            color="primary"
            size="small"
            onClick={() =>
              setDisplayMode(displayMode === "chat" ? "document" : "chat")
            }
          >
            {displayMode === "chat" ? <Grading /> : <QuestionAnswer />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Voir l'historique des traitements">
          <IconButton
            color="primary"
            size="small"
            disabled={!asset?.history}
            onClick={() =>
              dialogs.open(ProcessingHistoryDialog, {
                processingId: processing?._id,
                history: asset?.history ?? [],
                setProcessingId: setProcessingId,
              })
            }
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ajouter un document">
          <IconButton
            color="primary"
            size="small"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <Add />
          </IconButton>
        </Tooltip>
        {processing && processing?.status === Status.DONE && (
          <>
            <ExportMenu
              processingIds={[processing?._id]}
              disabled={!processing?.review}
              mini
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              style={{ display: "none" }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="dossier-status-label">Statut</InputLabel>
              <Select
                labelId="dossier-status-label"
                id="dossier-status-select"
                value={processing.workflow ?? ""}
                label="Statut"
                onChange={handleChangeStatus}
              >
                {processing.config.workflow.map((oneWorkflow) => (
                  <MenuItem key={oneWorkflow.name} value={oneWorkflow.name}>
                    {oneWorkflow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Box>
    </PageHeaderToolbar>
  );
}

function AssetPageHeader({
  asset,
  processing,
  setProcessingId,
  displayMode,
  setDisplayMode,
}: {
  asset: AssetAttrsDetails | undefined;
  processing: ProcessingAttrs | undefined;
  setProcessingId: Dispatch<SetStateAction<string | undefined>>;
  displayMode: "chat" | "document";
  setDisplayMode: Dispatch<SetStateAction<"chat" | "document">>;
}) {
  const PageToolbarComponent = useCallback(
    () =>
      asset && (
        <AssetPageToolbar
          asset={asset}
          processing={processing}
          setProcessingId={setProcessingId}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
        />
      ),
    [asset, processing, setProcessingId, displayMode, setDisplayMode]
  );

  return (
    <PageHeader
      breadcrumbs={
        asset
          ? [
              { title: "Dossiers", path: "/orion/assets" },
              {
                title: asset.name || "",
                path: `/orion/asset/${asset._id}`,
              },
            ]
          : []
      }
      title={asset?.name || ""}
      subheader={`Dernière modification: ${asset?.updated_date ? new Date(asset?.updated_date).toLocaleString() : "-"}`}
      slots={{ toolbar: PageToolbarComponent }}
    />
  );
}

export default AssetPageHeader;
